
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Ban, Clock, AlertTriangle } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { useTranslation } from 'react-i18next';

interface AccountStatus {
  status: 'active' | 'warned' | 'suspended' | 'banned';
  reason: string | null;
  suspended_until: string | null;
}

const AccountSuspended = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccountStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/auth');
          return;
        }

        const { data, error } = await supabase
          .from('user_account_status')
          .select('status, reason, suspended_until')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking account status:', error);
          return;
        }

        if (!data || data.status === 'active') {
          navigate('/');
          return;
        }

        setAccountStatus(data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAccountStatus();
  }, [navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'es' ? 'es-ES' : 
                   i18n.language === 'fr' ? 'fr-FR' :
                   i18n.language === 'de' ? 'de-DE' :
                   i18n.language === 'it' ? 'it-IT' :
                   i18n.language === 'pt' ? 'pt-BR' :
                   i18n.language === 'ja' ? 'ja-JP' :
                   i18n.language === 'ko' ? 'ko-KR' :
                   i18n.language === 'ru' ? 'ru-RU' :
                   i18n.language === 'zh' ? 'zh-CN' : 'en-US';
    
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('accountSuspended.loading')}</p>
        </div>
      </div>
    );
  }

  const isBanned = accountStatus?.status === 'banned';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="max-w-2xl w-full shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4">
            {isBanned ? (
              <div className="bg-red-100 dark:bg-red-900/30 rounded-full p-4 inline-block">
                <Ban className="h-12 w-12 text-red-600 dark:text-red-400" />
              </div>
            ) : (
              <div className="bg-orange-100 dark:bg-orange-900/30 rounded-full p-4 inline-block">
                <Clock className="h-12 w-12 text-orange-600 dark:text-orange-400" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">
            {isBanned ? t('accountSuspended.banned.title') : t('accountSuspended.suspended.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {isBanned ? (
            <>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="font-semibold text-red-900 dark:text-red-100">
                      {t('accountSuspended.banned.permanentMessage')}
                    </p>
                    <p className="text-sm text-red-800 dark:text-red-200">
                      {t('accountSuspended.banned.irreversible')}
                    </p>
                  </div>
                </div>
              </div>

              {accountStatus?.reason && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-muted-foreground">
                    {t('accountSuspended.banned.reason')}
                  </h3>
                  <div className="bg-muted rounded-lg p-4">
                    <p className="text-foreground">{accountStatus.reason}</p>
                  </div>
                </div>
              )}

              <div className="pt-4 space-y-3">
                <p className="text-sm text-muted-foreground text-center">
                  {t('accountSuspended.banned.contactSupport')}
                </p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={handleSignOut}>
                    {t('accountSuspended.signOut')}
                  </Button>
                  <Button onClick={() => navigate('/contact')}>
                    {t('accountSuspended.contactSupportButton')}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="font-semibold text-orange-900 dark:text-orange-100">
                      {t('accountSuspended.suspended.temporaryMessage')}
                    </p>
                    {accountStatus?.suspended_until ? (
                      <p className="text-sm text-orange-800 dark:text-orange-200">
                        {t('accountSuspended.suspended.accessAfter')}{' '}
                        <strong>{formatDate(accountStatus.suspended_until)}</strong>
                      </p>
                    ) : (
                      <p className="text-sm text-orange-800 dark:text-orange-200">
                        {t('accountSuspended.suspended.indefinite')}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {accountStatus?.reason && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-muted-foreground">
                    {t('accountSuspended.suspended.reason')}
                  </h3>
                  <div className="bg-muted rounded-lg p-4">
                    <p className="text-foreground">{accountStatus.reason}</p>
                  </div>
                </div>
              )}

              <div className="pt-4 space-y-3">
                <p className="text-sm text-muted-foreground text-center">
                  {t('accountSuspended.suspended.contactSupport')}
                </p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={handleSignOut}>
                    {t('accountSuspended.signOut')}
                  </Button>
                  <Button onClick={() => navigate('/contact')}>
                    {t('accountSuspended.contactSupportButton')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountSuspended;
